/**
 * NotificationBell — VP Feedback Loop (AC-vpfb-7/8/9/10/11)
 *
 * Header bell with unread-count badge. Click opens a dropdown listing
 * the current user's notifications newest-first (title + body snippet +
 * relative time). Clicking a notification item navigates to the related
 * work item and marks the notification read.
 *
 * AC-vpfb-9:  Opening the dropdown marks all visible notifications read.
 * AC-vpfb-10: Empty state when no notifications.
 * AC-vpfb-11: Only the authenticated user's own notifications are shown
 *             (enforced server-side; component simply renders what the API returns).
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import {
  notificationsService,
  type NotificationsListResult,
} from '@/services/roros/notifications.service';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';

// ─── Relative time helper ──────────────────────────────────────────────────

function timeAgo(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return t('Just now');
  if (diffMins < 60) return `${diffMins} ${t('minutes ago')}`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ${t('hours ago')}`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ${t('days ago')}`;
}

// ─── Query key ─────────────────────────────────────────────────────────────

export const NOTIFICATIONS_QUERY_KEY = ['roros', 'notifications'];

// ─── NotificationBell ──────────────────────────────────────────────────────

export function NotificationBell() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(PERMISSIONS.ROROS_NOTIFICATIONS_VIEW);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Data fetching ───────────────────────────────────────────────────────
  const { data } = useQuery<NotificationsListResult>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => notificationsService.list(),
    staleTime: 30_000,
    enabled: canView,
  });

  const unreadCount = data?.unreadCount ?? 0;
  const notifications = data?.items ?? [];

  // ── Mark all read mutation ──────────────────────────────────────────────
  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  // ── Mark one read mutation ──────────────────────────────────────────────
  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  // ── Close on outside click ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // ── AC-vpfb-9: open dropdown → mark all read ────────────────────────────
  const handleOpenToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && unreadCount > 0) {
      markAllReadMutation.mutate();
    }
  };

  // ── Click notification item → navigate + mark read ──────────────────────
  // AC-hard-1: use linkId for deep-link to the specific work item
  const handleItemClick = (notif: NotificationsListResult['items'][0]) => {
    setOpen(false);
    if (!notif.readAt) {
      markReadMutation.mutate(notif.id);
    }
    // Navigate to the specific work item in the control tower.
    // The ?item=<id> param is picked up by ControlTowerPage to auto-open
    // the WorkItemDrawer for that item (AC-hard-1 deep-link).
    if (notif.linkType === 'work_item' && notif.linkId) {
      navigate(`/roros/control-tower?item=${notif.linkId}`);
    }
  };

  if (!canView) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell trigger button */}
      <button
        type="button"
        aria-label={t('Notifications')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleOpenToggle}
        className={cn(
          'relative flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground',
          'hover:text-foreground hover:bg-muted transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        )}
        data-testid="notification-bell-button"
      >
        <Bell className="h-5 w-5" />
        {/* AC-vpfb-7: unread badge */}
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute top-1 right-1 flex items-center justify-center',
              'min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground',
              'text-[10px] font-bold leading-none px-1',
            )}
            aria-label={`${unreadCount} ${t('unread notifications')}`}
            data-testid="notification-bell-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={cn(
            'absolute right-0 mt-2 w-80 z-50',
            'bg-popover text-popover-foreground',
            'border border-border rounded-lg shadow-lg',
            'overflow-hidden',
          )}
          role="dialog"
          aria-label={t('Notifications')}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-foreground">{t('Notifications')}</span>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={() => markAllReadMutation.mutate()}
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                {t('Mark all read')}
              </button>
            )}
          </div>

          {/* Notification list */}
          <ul
            className="max-h-96 overflow-y-auto divide-y divide-border"
            data-testid="notification-list"
          >
            {notifications.length === 0 ? (
              /* AC-vpfb-10: empty state */
              <li
                className="px-4 py-8 text-center text-sm text-muted-foreground"
                data-testid="notification-empty-state"
              >
                {t("You're all caught up")}
              </li>
            ) : (
              notifications.map((notif) => (
                <li key={notif.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(notif)}
                    className={cn(
                      'w-full text-left px-4 py-3',
                      'hover:bg-muted/50 transition-colors',
                      !notif.readAt && 'bg-primary/5',
                    )}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {/* Unread dot */}
                      {!notif.readAt && (
                        <span
                          className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary"
                          aria-label={t('Unread')}
                        />
                      )}
                      <div className={cn('min-w-0 flex-1', notif.readAt && 'pl-4')}>
                        {/* Title */}
                        <p className="text-sm font-medium text-foreground truncate">
                          {notif.title}
                        </p>
                        {/* Body snippet */}
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notif.body}
                        </p>
                        {/* Relative time */}
                        <p className="text-xs text-muted-foreground mt-1">
                          {timeAgo(notif.createdAt)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
